import classNames from 'classnames'
import throttle from 'lodash/throttle'
import * as React from 'react'
import type {CSSProperties} from 'react'
import AutoSizer from 'react-virtualized/dist/es/AutoSizer'
import type {MeasuredCellParent} from 'react-virtualized/dist/es/CellMeasurer'
import CellMeasurer, {CellMeasurerCache} from 'react-virtualized/dist/es/CellMeasurer'
import type Grid from 'react-virtualized/dist/es/Grid'

import withHook from '../../../../hocs/withHook'
import type {BuildId} from '../../../../types'
import {focusSelfOrChildLink} from '../../../../utils/dom'
import {isEventWithSpecialKeyPressed} from '../../../../utils/keys'
import {getOpenPopupCount} from '../../../common/Popup/Popup.counter'
import {PipelinesTarget} from '../../../pages/PipelinesPages/contexts/targetContext'
import {buildLogKeyboardNavigationHint} from '../../Hints/HintItems/Hints.buildOverview'
import {useHint} from '../../Hints/Hints.hooks'
import {FullLogTarget} from '../BuildLog.types'
import type {
  BuildLogMessage,
  FullLogLoadingDirection,
  FullLogScrollBarInfo,
  LogView,
} from '../BuildLog.types'
import {
  getFirstExistIndex,
  getLastExistIndex,
  getNextExistIndex,
  getPrevExistIndex,
  showDuration,
} from '../BuildLog.utils'

import {
  calculateMessageHeight,
  calculateTotalMessagesHeight,
  getMessageWidth,
  ROW_HEIGHT,
} from './BuildLogMessages.utils'
import GridSelectionWrapper from './GridSelectionWrapper'
import LogMessage from './LogMessage/LogMessage'
import LogMessageActions from './LogMessageActions/LogMessageActions'
import LogMessageFocusWrapper from './LogMessageFocusWrapper/LogMessageFocusWrapper'
import LogMessageLoading from './LogMessageLoading/LogMessageLoading'
import LogMessageTime from './LogMessageTime/LogMessageTime'

import styles from './BuildLogMessages.css'

const CHECK_LOAD_THROTTLE = 1000
const UPDATE_SHOWN_THROTTLE = 500
const TAIL_COUNT_TO_LOAD = 300
const COUNT_TO_LOAD = 1000
const COUNT_TO_PRELOAD = 250
const columnIndex = 0
type ScrollAlignment = 'start' | 'end' | 'center'
type Props = {
  className?: string | null | undefined
  messages: ReadonlyArray<BuildLogMessage | null | undefined>
  holeIndexes: ReadonlyArray<number>
  buildId: BuildId
  onMessageToggle: (index: number, expand: boolean, withFocus?: boolean) => unknown
  onMessageSelect: (index: number) => unknown
  onShowMessages: (arg0: {
    firstDate: string | null | undefined
    lastDate: string | null | undefined
    firstId: number | null | undefined
    lastId: number | null | undefined
  }) => unknown
  loadMoreMessages: (id: number, count: number, throttle?: boolean) => unknown
  loadLastMessages: (count: number, force?: boolean) => unknown
  setScrollBarInfo: (arg0: FullLogScrollBarInfo) => unknown
  navigateToMessage: (index: number) => unknown
  toggleMessageSubtree: (index: number, expand: boolean) => unknown
  running?: boolean
  queued?: boolean
  focusLine?: number | null | undefined
  expandStateSet: Set<number>
  expandAll: boolean
  softWrapLines?: boolean
  relativeTime?: boolean
  maxMessageLength: number
  searchQuery: string | null | undefined
  searchFoundId: number | null | undefined
  searchMatchPosition: number | null | undefined
  target: FullLogTarget
  pipelinesTarget?: PipelinesTarget | null | undefined
  lastLoadingDirection?: FullLogLoadingDirection
  isAutodetectTimeZone: boolean
  logView: LogView
}
type State = {
  keyBoardMode: boolean
  needUpdate: boolean
  scrollTop: number
  scrollLeft: number
  messagesCount: number
  messagesHeight: number
  gridWidth: number
  scrollBarWidth: number
  childrenLoading: ReadonlySet<number>
  searchFoundKey: string | null
  scrollToSearch: boolean
}

class FullBuildLogMessages extends React.PureComponent<Props, State> {
  static getDerivedStateFromProps(nextProps: Props, prevState: State): Partial<State> | null {
    if (prevState.messagesCount !== nextProps.messages.length) {
      const newHeight = calculateTotalMessagesHeight(
        nextProps.messages,
        prevState.gridWidth - prevState.scrollBarWidth,
        nextProps.softWrapLines === true,
        nextProps.running === true,
        nextProps.target,
        nextProps.pipelinesTarget,
      )
      const scrollOffset =
        nextProps.lastLoadingDirection === 'up' ? newHeight - prevState.messagesHeight : 0
      let needUpdate = false

      if (nextProps.running === true || nextProps.queued === true) {
        needUpdate = true
      } else if (prevState.needUpdate) {
        const lastMessage = nextProps.messages[nextProps.messages.length - 1]

        if (lastMessage?.isLast !== true) {
          needUpdate = true
        }
      }

      return {
        scrollTop: prevState.scrollTop + scrollOffset,
        messagesCount: nextProps.messages.length,
        messagesHeight: newHeight,
        needUpdate,
      }
    }

    const searchFoundKey =
      nextProps.searchFoundId != null && nextProps.searchMatchPosition != null
        ? `${nextProps.searchFoundId}-${nextProps.searchMatchPosition}`
        : null
    if (searchFoundKey !== prevState.searchFoundKey) {
      return {scrollToSearch: true, searchFoundKey}
    }

    return null
  }

  state: State = {
    keyBoardMode: false,
    scrollTop: 0,
    scrollLeft: 0,
    messagesCount: 0,
    messagesHeight: 0,
    gridWidth: 0,
    scrollBarWidth: 0,
    needUpdate: false,
    childrenLoading: new Set(),
    searchFoundKey: null,
    scrollToSearch: false,
  }

  ref: {
    current: null | HTMLDivElement
  } = React.createRef<HTMLDivElement>()

  grid: Grid | null | undefined = null
  firstRenderedIndex = -1
  lastRenderedIndex = -1
  renderedInfo:
    | {
        firstDate: string | null | undefined
        lastDate: string | null | undefined
        firstId: number | null | undefined
        lastId: number | null | undefined
      }
    | null
    | undefined

  scrollDirection: 'up' | 'down' = 'down'
  updateTimeout: number | null | undefined
  loadMessagesIfNecessary: () => void = throttle(() => {
    const {holeIndexes, messages, loadMoreMessages, focusLine, searchFoundId} = this.props
    holeIndexes.forEach((holeIndex: number) => {
      if (this.state.needUpdate && holeIndex === this.lastRenderedIndex) {
        return
      }

      if (
        holeIndex > this.firstRenderedIndex - COUNT_TO_PRELOAD &&
        holeIndex < this.lastRenderedIndex + COUNT_TO_PRELOAD
      ) {
        const nextMessage = messages[getNextExistIndex(messages, holeIndex)]

        if (
          (this.scrollDirection === 'up' ||
            holeIndex === 0 ||
            (focusLine === searchFoundId && searchFoundId != null && searchFoundId > holeIndex)) &&
          nextMessage != null
        ) {
          loadMoreMessages(nextMessage.id, -COUNT_TO_LOAD)
        } else {
          const previousMessage = messages[getPrevExistIndex(messages, holeIndex)]

          if (previousMessage != null) {
            loadMoreMessages(previousMessage.id, COUNT_TO_LOAD)
          }
        }
      }
    })
  }, CHECK_LOAD_THROTTLE)

  onShowMessagesThrottled: () => void = throttle(() => {
    if (this.renderedInfo != null) {
      this.props.onShowMessages(this.renderedInfo)
    }
  }, UPDATE_SHOWN_THROTTLE)

  componentDidMount() {
    const {messages, focusLine, running, searchFoundId} = this.props
    let focusLineIndex =
      running === true ? getLastExistIndex(messages) : getFirstExistIndex(messages)
    let scrollLineIndex = running === true ? messages.length - 1 : focusLineIndex

    if (focusLine != null && focusLine !== messages[focusLineIndex]?.id) {
      const found = messages.findIndex(
        (item: BuildLogMessage | null | undefined) => item != null && item.id === focusLine,
      )

      if (found > -1) {
        focusLineIndex = found
        scrollLineIndex = found
      }
    }

    requestAnimationFrame(() => {
      if (searchFoundId != null && searchFoundId === focusLine) {
        this.scrollToRow(scrollLineIndex, 'center')
      } else {
        this.focusRow(focusLineIndex, scrollLineIndex, 'start')
      }

      this.loadMessagesIfNecessary()
    })

    document.addEventListener('keydown', this.onGlobalKeyDown)
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.buildId !== prevProps.buildId) {
      this.reset()
    } else if (
      (this.props.messages !== prevProps.messages ||
        this.state.gridWidth !== prevState.gridWidth ||
        this.state.scrollBarWidth !== prevState.scrollBarWidth ||
        this.props.softWrapLines !== prevProps.softWrapLines ||
        this.props.relativeTime !== prevProps.relativeTime) &&
      this.grid != null
    ) {
      this.grid.recomputeGridSize({
        columnIndex,
      })
    }

    if (
      prevProps.running === true &&
      this.props.running === false &&
      this.lastRenderedIndex === this.props.messages.length - 1
    ) {
      this.loadTailMessages(true)
    }

    if (
      this.props.searchFoundId !== prevProps.searchFoundId &&
      this.props.searchFoundId === this.props.focusLine &&
      this.props.focusLine != null
    ) {
      this.scrollToRowByMessageId(this.props.focusLine)
    }
  }

  componentWillUnmount() {
    if (this.updateTimeout != null) {
      clearTimeout(this.updateTimeout)
    }

    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('keydown', this.onGlobalKeyDown)
  }

  rowSizeCacheKeyMapper: (rowIndex: number) => null | string = (rowIndex: number) => {
    const message: BuildLogMessage | null | undefined = this.props.messages[rowIndex]
    return message != null ? `message-${message.id}` : null
  }

  /* eslint-disable sort-class-members/sort-class-members */
  rowHeightCache: CellMeasurerCache = new CellMeasurerCache({
    minHeight: ROW_HEIGHT,
    fixedWidth: true,
    keyMapper: this.rowSizeCacheKeyMapper,
  })

  rowWidthCache: CellMeasurerCache = new CellMeasurerCache({
    fixedHeight: true,
    keyMapper: this.rowSizeCacheKeyMapper,
  })
  /* eslint-enable */

  setGridRef: (arg0: Grid | null | undefined) => void = el => {
    this.grid = el
  }

  onSectionRendered = ({
    rowStartIndex,
    rowStopIndex,
  }: {
    rowStartIndex: number
    rowStopIndex: number
  }): void => {
    const {messages} = this.props
    this.scrollDirection =
      rowStartIndex < this.firstRenderedIndex || rowStopIndex < this.lastRenderedIndex
        ? 'up'
        : 'down'

    const firstVisibleIndex = getNextExistIndex(messages, rowStartIndex - 1)
    const {id: firstId, timestamp: firstDate} = messages[firstVisibleIndex] ?? {}
    const lastVisibleIndex = getPrevExistIndex(messages, rowStopIndex + 1)
    const {id: lastId, timestamp: lastDate} = messages[lastVisibleIndex] ?? {}

    this.renderedInfo = {firstId, firstDate, lastId, lastDate}
    this.firstRenderedIndex = rowStartIndex
    this.lastRenderedIndex = rowStopIndex
    this.loadMessagesIfNecessary()
    this.loadTailMessagesIfNecessary()
    this.onShowMessagesThrottled()
  }

  loadTailMessages = (force = false): void => {
    const {messages, focusLine, loadLastMessages, loadMoreMessages} = this.props
    const lastExistMessage = messages[getLastExistIndex(messages)]
    if (
      messages.length < 2 ||
      lastExistMessage == null ||
      focusLine == null ||
      focusLine === lastExistMessage.id ||
      focusLine < (messages[this.firstRenderedIndex]?.id ?? 0)
    ) {
      loadLastMessages(TAIL_COUNT_TO_LOAD, force)
    } else {
      loadMoreMessages(lastExistMessage.id, TAIL_COUNT_TO_LOAD, true)
    }
  }

  loadTailMessagesIfNecessary = (): void => {
    const {needUpdate} = this.state

    if (needUpdate) {
      const {messages} = this.props

      if (this.lastRenderedIndex === messages.length - 1) {
        this.loadTailMessages()

        if (this.updateTimeout != null) {
          clearTimeout(this.updateTimeout)
        }

        this.updateTimeout = window.setTimeout(
          this.loadTailMessagesIfNecessary,
          CHECK_LOAD_THROTTLE,
        )
      }
    }
  }

  onGridResize = ({width}: {height: number; width: number}): void => {
    if (width !== this.state.gridWidth) {
      this.rowHeightCache.clearAll()
      this.setState(state => ({
        gridWidth: width,
        messagesHeight: calculateTotalMessagesHeight(
          this.props.messages,
          width - state.scrollBarWidth,
          this.props.softWrapLines === true,
          this.props.running === true,
          this.props.target,
          this.props.pipelinesTarget,
        ),
      }))
    }
  }

  handleMessageClick = (event: React.MouseEvent, id: number) => {
    if (!isEventWithSpecialKeyPressed(event)) {
      this.props.onMessageSelect(id)
    }
  }

  handleActionsClick = (event: React.MouseEvent, id: number) => {
    focusSelfOrChildLink(event.currentTarget.parentElement?.parentElement)
    event.stopPropagation()

    if (!isEventWithSpecialKeyPressed(event)) {
      event.preventDefault()
      this.props.onMessageSelect(id)
    }
  }

  handleLinkedMessageClick: (arg0: React.MouseEvent, arg1: number) => void = (event, id) => {
    event.stopPropagation()

    if (!isEventWithSpecialKeyPressed(event)) {
      event.preventDefault()
      const {messages, navigateToMessage} = this.props
      const searchIndex = messages.findIndex(
        (item: BuildLogMessage | null | undefined) => item != null && item.id === id,
      )

      if (searchIndex > -1) {
        this.focusRow(searchIndex)
      } else {
        navigateToMessage(id)
      }
    }
  }

  onGlobalKeyDown = (event: KeyboardEvent) => {
    if (
      this.props.messages.length > 1 &&
      !window.BS?.Hider?.hasVisiblePopups() &&
      getOpenPopupCount() === 0 &&
      event.target instanceof HTMLElement &&
      !event.target.isContentEditable &&
      !['INPUT', 'TEXTAREA', 'SELECT', 'OPTION'].includes(event.target.tagName.toUpperCase())
    ) {
      this.handleKeyDown(event, null, false)
    }
  }

  onMessageKeyDown = (event: React.KeyboardEvent | KeyboardEvent, index: number | null) => {
    if (event.target === event.currentTarget && event.target instanceof HTMLElement) {
      this.handleKeyDown(event, index, true)
    }
  }

  handleKeyDown = (
    event: React.KeyboardEvent | KeyboardEvent,
    index: number | null,
    prevent: boolean,
  ) => {
    const {messages, onMessageSelect} = this.props
    const message: BuildLogMessage | null | undefined = index != null ? messages[index] : null

    const modifier = navigator.platform === 'MacIntel' ? event.metaKey : event.ctrlKey

    switch (event.key) {
      case 'Home':
        this.focusFirstMessage()
        break

      case 'End':
        this.focusLastMessage()
        break

      case 'ArrowUp':
        if (modifier) {
          this.focusFirstMessage()
        } else if (index != null && !event.shiftKey) {
          this.focusRow(getPrevExistIndex(messages, index))
        } else {
          return
        }
        break

      case 'ArrowDown':
        if (modifier) {
          this.focusLastMessage()
        } else if (index != null && !event.shiftKey) {
          this.focusRow(getNextExistIndex(messages, index))
        } else {
          return
        }
        break

      case 'ArrowLeft':
        if (message == null || index == null || event.shiftKey) {
          return
        }
        if (message.containsMessages === true && this.isExpanded(index)) {
          if (modifier) {
            this.toggleMessageSubtree(message.id, false)
          } else {
            this.onMessageToggle(message.id, false, false, message.childrenEmpty)
          }
        } else if (message.parentId != null) {
          const parentId = message.parentId
          const parentIndex = messages.findIndex(
            (item: BuildLogMessage | null | undefined) => item != null && item.id === parentId,
          )
          this.focusRow(parentIndex > -1 ? parentIndex : getPrevExistIndex(messages, index))
        } else {
          this.focusRow(getPrevExistIndex(messages, index))
        }
        break

      case 'ArrowRight':
        if (message == null || index == null || event.shiftKey) {
          return
        }
        if (message.containsMessages === true && !this.isExpanded(index)) {
          if (modifier) {
            this.toggleMessageSubtree(message.id, true)
          } else {
            this.onMessageToggle(message.id, true, false, message.childrenEmpty)
          }
          break
        }
        this.focusRow(getNextExistIndex(messages, index))
        break

      case 'Enter':
        if (message == null || index == null) {
          return
        }
        if (message.containsMessages === true) {
          this.onMessageToggle(message.id, !this.isExpanded(index), true, message.childrenEmpty)
        } else {
          onMessageSelect(message.id)
        }
        break

      case ' ':
        if (event.shiftKey) {
          this.focusPreviousPage()
        } else {
          this.focusNextPage()
        }
        break

      case 'PageUp':
        this.focusPreviousPage()
        break

      case 'PageDown':
        this.focusNextPage()
        break

      default:
        return
    }

    if (prevent) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.setState({
      keyBoardMode: true,
      scrollToSearch: false,
    })
    document.addEventListener('mousemove', this.onMouseMove)
  }

  focusFirstMessage: () => void = () => {
    const {messages, navigateToMessage} = this.props

    if (messages[0] == null) {
      navigateToMessage(0)
    } else {
      this.focusRow(0)
    }
  }

  focusLastMessage: () => void = () => {
    const {messages, navigateToMessage} = this.props
    const lastIndex = messages.length - 1

    if (messages[lastIndex] == null) {
      navigateToMessage(-1)
    } else {
      this.focusRow(lastIndex)
    }
  }

  focusPreviousPage: () => void = () => {
    if (this.firstRenderedIndex > -1) {
      this.focusRow(
        getNextExistIndex(this.props.messages, this.firstRenderedIndex),
        this.firstRenderedIndex + 1,
        'end',
      )
    }
  }

  focusNextPage: () => void = () => {
    if (this.lastRenderedIndex > -1) {
      this.focusRow(
        getPrevExistIndex(this.props.messages, this.lastRenderedIndex),
        this.lastRenderedIndex - 1,
        'start',
      )
    }
  }

  focusRow: (
    indexToFocus?: number | null,
    indexToScroll?: number | null,
    alignment?: ScrollAlignment | null,
  ) => void = (indexToFocus, indexToScroll, alignment) => {
    if (indexToFocus == null) {
      return
    }

    this.scrollToRow(indexToScroll ?? indexToFocus, alignment)
    const focusId = this.props.messages[indexToFocus]?.id

    if (focusId != null) {
      requestAnimationFrame(() => {
        if (this.ref.current != null) {
          focusSelfOrChildLink(this.ref.current.querySelector(`[data-id="${focusId}"]`))
        }
      })
    }
  }

  scrollToRow: (indexToScroll: number, alignment: ScrollAlignment | null | undefined) => void = (
    indexToScroll,
    alignment,
  ) => {
    if (this.grid != null) {
      const {scrollTop} = this.grid.getOffsetForCell({
        alignment: alignment ?? 'auto',
        rowIndex: indexToScroll,
        columnIndex,
      })

      this.setState({
        scrollTop,
      })
    }
  }

  isExpanded: (index: number) => boolean = (index: number): boolean => {
    const {messages, expandStateSet, expandAll} = this.props
    const message = messages[index]

    if (message == null) {
      return false
    }

    return expandAll ? !expandStateSet.has(message.id) : expandStateSet.has(message.id)
  }

  onMouseMove: () => void = () => {
    this.setState({
      keyBoardMode: false,
    })
    document.removeEventListener('mousemove', this.onMouseMove)
  }

  onScroll = ({scrollTop, scrollLeft}: {scrollTop: number; scrollLeft: number}): void => {
    if (
      this.state.scrollTop !== scrollTop ||
      this.state.scrollLeft !== scrollLeft ||
      this.state.scrollToSearch
    ) {
      this.setState({
        scrollTop,
        scrollLeft,
        scrollToSearch: false,
      })
    }
  }

  onScrollbarPresenceChange: (arg0: {
    horizontal: boolean
    size: number
    vertical: boolean
  }) => void = ({
    size,
    vertical,
    horizontal,
  }: {
    size: number
    vertical: boolean
    horizontal: boolean
  }) => {
    const scrollBarWidth = vertical ? size : 0
    this.setState(state => ({
      scrollBarWidth,
      messagesHeight: calculateTotalMessagesHeight(
        this.props.messages,
        state.gridWidth - scrollBarWidth,
        this.props.softWrapLines === true,
        this.props.running === true,
        this.props.target,
        this.props.pipelinesTarget,
      ),
    }))
    this.props.setScrollBarInfo({
      size,
      vertical,
      horizontal,
    })
  }

  calculateSoftWrapRowHeight: (row: {index: number}) => number = ({index}) => {
    if (this.rowHeightCache.has(index, 0)) {
      return this.rowHeightCache.getHeight(index, 0)
    }

    const {messages, target, pipelinesTarget, running} = this.props
    const {gridWidth, scrollBarWidth} = this.state
    return calculateMessageHeight(
      messages[index],
      gridWidth - scrollBarWidth,
      index === messages.length - 1,
      true,
      running === true,
      target,
      pipelinesTarget,
    )
  }

  calculateNoWrapRowHeight: (arg0: {index: number}) => number = ({index}: {index: number}) => {
    const {messages, target, pipelinesTarget, running} = this.props
    return calculateMessageHeight(
      messages[index],
      null,
      index === messages.length - 1,
      false,
      running === true,
      target,
      pipelinesTarget,
    )
  }

  cellRenderer: (props: {
    rowIndex: number
    style: CSSProperties
    parent: MeasuredCellParent
  }) => React.ReactNode = ({rowIndex, style, parent}) => {
    const {
      messages,
      buildId,
      focusLine,
      running,
      queued,
      softWrapLines,
      relativeTime,
      searchQuery,
      searchFoundId,
      searchMatchPosition,
      logView,
      pipelinesTarget,
    } = this.props
    const message: BuildLogMessage | null | undefined = messages[rowIndex]

    if (message == null) {
      const isLast = rowIndex === messages.length - 1
      const previousMessage = messages[rowIndex - 1]
      const nextMessage = messages[rowIndex + 1]
      const runningMessage = running === true && isLast
      const showLoading = queued !== true || !isLast
      const key = runningMessage
        ? `hole-running-${rowIndex}`
        : `hole-${previousMessage?.id ?? -1}-${nextMessage?.id ?? 0}-${rowIndex}`
      return (
        <div style={style} key={key}>
          {showLoading && (
            <LogMessageLoading
              className={classNames(styles.message, {
                [styles.runningMessage]: runningMessage,
              })}
              level={previousMessage?.level ?? nextMessage?.level}
              running={runningMessage}
              buildId={buildId}
            />
          )}
        </div>
      )
    }

    const {childrenLoading, scrollToSearch} = this.state
    const expandable = message.containsMessages === true
    const expanded = expandable && this.isExpanded(rowIndex)
    const key = `message-${message.id}-${rowIndex}`

    const isPipelineContext =
      pipelinesTarget === PipelinesTarget.JOB_DETAILS_POPUP ||
      pipelinesTarget === PipelinesTarget.JOB_DETAILS_SIDEBAR

    const row = (
      <LogMessageFocusWrapper
        key={key}
        id={message.id}
        index={rowIndex}
        style={style}
        className={classNames(styles.messageFocusWrapper, {
          [styles.stepHeading]: message.blockType === 'teamcity-build-step-type',
        })}
        onKeyDown={this.onMessageKeyDown}
        level={message.level}
        expanded={expanded}
        onClick={this.handleMessageClick}
      >
        {message.timestamp != null && (
          <>
            {isPipelineContext && (
              <span className={styles.actions} role="cell" data-log-message-actions>
                <LogMessageActions
                  timestamp={message.timestamp}
                  serverTimestamp={message.serverTimestamp}
                  text={message.text}
                  expandable={expandable}
                  expanded={expanded}
                  id={message.id}
                  onClick={this.handleActionsClick}
                  toggleMessageSubtree={this.toggleMessageSubtree}
                />
              </span>
            )}
            <span className={styles.time} role="cell" data-log-message-time>
              <LogMessageTime
                timestamp={message.timestamp}
                serverTimestamp={message.serverTimestamp}
                text={message.text}
                expandable={expandable}
                expanded={expanded}
                id={message.id}
                onClick={this.handleActionsClick}
                toggleMessageSubtree={this.toggleMessageSubtree}
                highlight={message.id === focusLine}
                logView={logView}
                relativeTime={relativeTime}
              />
            </span>
          </>
        )}
        <LogMessage
          className={styles.message}
          id={message.id}
          level={message.level}
          expandable={expandable}
          expanded={expanded}
          text={message.text}
          duration={message.duration}
          status={message.status}
          blockType={message.blockType}
          childrenEmpty={message.childrenEmpty}
          childrenLoading={message.childrenEmpty === true && childrenLoading.has(message.id)}
          onMessageToggle={this.onMessageToggle}
          parseLinks={message.containUrl}
          parseAnsi={message.containAnsi}
          linkedMessageId={message.linkedMessageId}
          onLinkedMessageClick={this.handleLinkedMessageClick}
          verbose={message.verbose}
          breakLines={softWrapLines === true}
          role="cell"
          searchQuery={searchQuery}
          searchMatchPosition={searchFoundId === message.id ? searchMatchPosition : null}
          scrollToSearch={scrollToSearch}
          treeView
          showDuration={showDuration(message)}
          timestamp={message.serverTimestamp ?? message.timestamp}
          logView={logView}
          running={running}
        />
        <br />
      </LogMessageFocusWrapper>
    )
    return message.inaccurateSize === true ? (
      <CellMeasurer
        cache={softWrapLines === true ? this.rowHeightCache : this.rowWidthCache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={rowIndex}
      >
        {row}
      </CellMeasurer>
    ) : (
      row
    )
  }

  onMessageToggle: (
    index: number,
    expand: boolean,
    withFocus?: boolean,
    childrenEmpty?: boolean | null,
  ) => void = (
    index: number,
    expand: boolean,
    withFocus?: boolean,
    childrenEmpty?: boolean | null,
  ) => {
    if (childrenEmpty === true && expand) {
      this.setState((state: State) => ({
        childrenLoading: new Set<number>([...state.childrenLoading, index]),
      }))
    }

    this.props.onMessageToggle(index, expand, withFocus)
  }

  toggleMessageSubtree = (index: number, expand: boolean) => {
    if (expand) {
      this.setState(state => ({
        childrenLoading: new Set<number>([...state.childrenLoading, index]),
      }))
    }

    this.props.toggleMessageSubtree(index, expand)
  }

  getColumnWidth: (wrapperWidth: number) => number = (wrapperWidth: number) => {
    const {scrollBarWidth} = this.state
    const {softWrapLines, maxMessageLength, target, pipelinesTarget} = this.props
    const regularWidth = wrapperWidth - scrollBarWidth

    if (softWrapLines === true) {
      return regularWidth
    }

    const maxMeasuredWidth = this.rowWidthCache.columnWidth({
      index: 0,
    })
    const calculatedColumnWidth = getMessageWidth(maxMessageLength, target, pipelinesTarget)
    return Math.max(regularWidth, maxMeasuredWidth, calculatedColumnWidth)
  }

  scrollToRowByMessageId = (messageId: number) => {
    const found = this.props.messages.findIndex(
      (item: BuildLogMessage | null | undefined) => item != null && item.id === messageId,
    )

    if (found > -1) {
      this.scrollToRow(found, 'center')
    }
  }

  reset() {
    this.firstRenderedIndex = -1
    this.lastRenderedIndex = -1
    this.scrollDirection = 'down'
  }

  render(): React.ReactNode {
    const {
      className,
      messages,
      focusLine,
      softWrapLines,
      searchQuery,
      searchFoundId,
      target,
      pipelinesTarget,
      searchMatchPosition,
      isAutodetectTimeZone,
    } = this.props
    const {keyBoardMode, scrollTop, scrollLeft, messagesHeight, childrenLoading} = this.state
    return (
      <div
        data-hint-container-id={buildLogKeyboardNavigationHint.id}
        ref={this.ref}
        className={classNames(styles.messages, className, {
          [styles.hoverDisabled]: keyBoardMode,
          [styles.pageTarget]: target === FullLogTarget.PAGE,
          [styles.jobDetailsPopupTarget]: pipelinesTarget === PipelinesTarget.JOB_DETAILS_POPUP,
          [styles.jobDetailsSidebarTarget]: pipelinesTarget === PipelinesTarget.JOB_DETAILS_SIDEBAR,
        })}
      >
        <AutoSizer onResize={this.onGridResize}>
          {({width, height}) => (
            <GridSelectionWrapper
              width={width}
              columnWidth={this.getColumnWidth(width)}
              height={height}
              scrollTop={messagesHeight > height ? scrollTop : 0}
              scrollLeft={scrollLeft}
              onScroll={this.onScroll}
              onScrollbarPresenceChange={this.onScrollbarPresenceChange}
              overscanRowCount={Math.ceil(height / ROW_HEIGHT) * 2}
              aria-label="Build Log"
              className={styles.grid}
              role="treegrid"
              ref={this.setGridRef}
              cellRenderer={this.cellRenderer}
              messages={messages}
              scrollToRowByMessageId={this.scrollToRowByMessageId}
              rowCount={messages.length}
              rowHeight={
                softWrapLines === true
                  ? this.calculateSoftWrapRowHeight
                  : this.calculateNoWrapRowHeight
              }
              onSectionRendered={this.onSectionRendered}
              columnCount={1}
              // we need to pass these props to trigger updates when they change
              focusLine={focusLine}
              childrenLoading={childrenLoading}
              softWrapLines={softWrapLines}
              searchQuery={searchQuery}
              searchFoundId={searchFoundId}
              searchMatchPosition={searchMatchPosition}
              isAutodetectTimeZone={isAutodetectTimeZone}
            />
          )}
        </AutoSizer>
      </div>
    )
  }
}

export default withHook<void, Props>(() => useHint(buildLogKeyboardNavigationHint))(
  FullBuildLogMessages,
)
